import { useState, useEffect } from "react";
import { db } from "../../config/firebase";
import { doc, collection, onSnapshot, getDoc } from "firebase/firestore";

/**
 * Fetch single document from Firestore with real-time updates
 * @param {string} collectionName - Collection name (e.g., "home", "about")
 * @param {string} docId - Document ID (usually "main")
 */
export const useFirestoreDoc = (collectionName, docId = "main") => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    const docRef = doc(db, collectionName, docId);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setData(docSnap.data());
        } else {
          console.warn(`No document found: ${collectionName}/${docId}`);
          setData(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error(`Error fetching ${collectionName}/${docId}:`, err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [collectionName, docId]);

  return { data, loading, error };
};

/**
 * Fetch entire collection from Firestore with real-time updates
 * @param {string} collectionName - Collection name (e.g., "certificates")
 */
export const useFirestoreCollection = (collectionName) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    const collectionRef = collection(db, collectionName);

    const unsubscribe = onSnapshot(
      collectionRef,
      (querySnapshot) => {
        const items = [];
        querySnapshot.forEach((doc) => {
          items.push({ id: doc.id, ...doc.data() });
        });
        setData(items);
        setLoading(false);
      },
      (err) => {
        console.error(`Error fetching ${collectionName}:`, err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [collectionName]);

  return { data, loading, error };
};

/**
 * Hook for Home section data
 * Uses one-time read with 24-hour caching (not real-time listener)
 */
export const useHomeData = () => useFirestoreDocOnce("home");

/**
 * Hook for About section data
 * Uses one-time read with 24-hour caching (not real-time listener)
 */
export const useAboutData = () => useFirestoreDocOnce("about");

/**
 * Hook for Skills section data
 * Uses one-time read with 24-hour caching (not real-time listener)
 */
export const useSkillsData = () => useFirestoreDocOnce("skills");

/**
 * Hook for Qualification section data
 * Uses one-time read with 24-hour caching (not real-time listener)
 */
export const useQualificationData = () => useFirestoreDocOnce("qualification");

/**
 * Hook for Certificates section data
 * Uses one-time read with 24-hour caching (not real-time listener)
 */
export const useCertificatesData = () => useFirestoreDocOnce("certificates");

/**
 * Hook for Projects section data
 * Uses one-time read with 24-hour caching (not real-time listener)
 */
export const useProjectsData = () => useFirestoreDocOnce("projects");

/**
 * Hook for Contact section data
 * Uses one-time read with 24-hour caching (not real-time listener)
 */
export const useContactData = () => useFirestoreDocOnce("contact");

/**
 * Hook for Default Themes data
 * Uses one-time read with 24-hour caching (not real-time listener)
 */
export const useDefaultThemesData = () => useFirestoreDocOnce("defaultThemes");

/**
 * Helper: Format timestamp with readable date/time and next refresh time
 */
export const formatCacheTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(parseInt(timestamp));
  const nextRefresh = new Date(parseInt(timestamp) + 1000 * 60 * 60 * 24); // 24 hours later

  // Calculate remaining time in hours and minutes
  const totalSeconds = Math.max(0, (nextRefresh - Date.now()) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const timeRemainingStr = `${hours}h ${minutes}m`;

  return {
    cachedAt: date.toLocaleString(), // e.g., "12/13/2025, 2:30:45 PM"
    nextRefreshAt: nextRefresh.getTime(), // raw ms — parsed safely by formatCacheTime
    timeRemaining: timeRemainingStr, // e.g., "24h 0m" or "23h 45m"
  };
};

/**
 * Helper: Invalidate cache when admin publishes
 * Call this after any publish operation to clear cached data
 */
export const invalidateCache = (collectionName, docId = "main") => {
  try {
    const localDb = JSON.parse(
      localStorage.getItem("portfolio_local_db") || "{}",
    );
    // Remove the section from cache
    delete localDb[collectionName];
    localStorage.setItem("portfolio_local_db", JSON.stringify(localDb));
    console.log(`Cache invalidated for ${collectionName}/${docId}`);
  } catch (error) {
    console.error("Error invalidating cache:", error);
  }
};

/**
 * Fetch single document from Firestore ONCE with smart localStorage caching
 * Cache expires after 24 hours OR when invalidateCache() is called (on publish)
 * Uses portfolio_local_db structure for unified cache management
 * Significantly reduces read quota
 */
export const useFirestoreDocOnce = (collectionName, docId = "main") => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null); // { cachedAt, nextRefreshAt, timeRemaining }

  useEffect(() => {
    let isMounted = true;
    const CACHE_DURATION = 1000 * 60 * 60 * 24; // 24 hours for production
    const DEBUG = false; // Set to true to enable debug logs

    const loadData = async () => {
      try {
        // Try to load from portfolio_local_db cache first
        const localDb = JSON.parse(
          localStorage.getItem("portfolio_local_db") || "{}",
        );

        const cached = localDb[collectionName];
        const now = Date.now();

        // Check if cache is fresh (not expired)
        if (cached && cached.timestamp) {
          const cacheTimestamp = new Date(cached.timestamp).getTime();
          const cacheAge = now - cacheTimestamp;
          const isCacheFresh = cacheAge < CACHE_DURATION;

          if (isCacheFresh) {
            try {
              if (isMounted) {
                setData(cached.data);
                setCacheInfo(formatCacheTimestamp(cacheTimestamp.toString())); // Set cache metadata
                setLoading(false);
                if (DEBUG) {
                  console.log(`✅ [${collectionName}] Using CACHED data`);
                }
              }
              return; // Cache is fresh, don't fetch
            } catch (e) {
              console.error("Cache parse error:", e);
            }
          } else {
            if (DEBUG) {
              console.log(
                `⏰ [${collectionName}] Cache EXPIRED - fetching fresh data...`,
              );
            }
          }
        } else {
          if (DEBUG) {
            console.log(
              `📭 [${collectionName}] No cached data found - fetching from Firestore...`,
            );
          }
        }

        // Cache expired or missing → Fetch fresh data from Firestore (ONE-TIME READ)
        if (isMounted) {
          setNetworkLoading(true);
        }
        const docRef = doc(db, collectionName, docId);
        const docSnap = await getDoc(docRef);

        if (!isMounted) return;

        if (docSnap.exists()) {
          const docData = docSnap.data();
          setData(docData);

          // Update portfolio_local_db with new data and timestamp
          const updatedLocalDb = JSON.parse(
            localStorage.getItem("portfolio_local_db") || "{}",
          );
          updatedLocalDb[collectionName] = {
            data: docData,
            timestamp: new Date().toISOString(),
          };
          localStorage.setItem(
            "portfolio_local_db",
            JSON.stringify(updatedLocalDb),
          );

          if (DEBUG) {
            console.log(
              `🔄 [${collectionName}] FETCHED fresh data from Firestore`,
            );
          }

          // Set cache metadata
          const newTimestamp = Date.now().toString();
          setCacheInfo(formatCacheTimestamp(newTimestamp));
        } else {
          console.warn(`No document found: ${collectionName}/${docId}`);
          setData(null);
        }
        if (isMounted) {
          setLoading(false);
          setNetworkLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error(`Error fetching ${collectionName}/${docId}:`, err);
          setError(err);
          setLoading(false);
          setNetworkLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [collectionName, docId]);

  // Manual refresh function for admins
  const refresh = async () => {
    setLoading(true);
    setNetworkLoading(true);
    try {
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const docData = docSnap.data();
        setData(docData);

        // Update portfolio_local_db with new data and timestamp
        const updatedLocalDb = JSON.parse(
          localStorage.getItem("portfolio_local_db") || "{}",
        );
        const newTimestamp = new Date().toISOString();
        updatedLocalDb[collectionName] = {
          data: docData,
          timestamp: newTimestamp,
        };
        localStorage.setItem(
          "portfolio_local_db",
          JSON.stringify(updatedLocalDb),
        );

        // Update cache metadata
        const newTimestampMs = Date.now().toString();
        setCacheInfo(formatCacheTimestamp(newTimestampMs));
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setNetworkLoading(false);
    }
  };

  return { data, loading, networkLoading, error, refresh, cacheInfo };
};
